/**
 * Medição dos blocos do documento impresso, no DOM.
 *
 * Os blocos são renderizados uma vez num quadro (iframe) com a largura da
 * folha, onde as regras de mídia valem como no papel; daqui saem a altura de
 * cada um e os pontos onde ele pode ser cortado, que `paginar.ts` usa só
 * quando o bloco é maior que uma folha inteira.
 *
 * O elemento medido vive em OUTRO documento: nada aqui usa `window`,
 * `document` nem `instanceof HTMLElement` do documento principal; tudo sai
 * do `ownerDocument` do próprio elemento.
 *
 * Corte seguro: o topo de uma "linha" de filhos (filhos lado a lado contam
 * como uma linha só) de um elemento que não tem texto solto nem filhos em
 * linha de texto. Assim o corte cai entre cartões, entre linhas de tabela ou
 * entre linhas de uma grade — nunca no meio de uma linha de texto.
 *
 * Item do catálogo inteiro: todo item da documentação tem uma âncora
 * `[data-search-id]` (showcase, amostra de cor, bloco de texto). Corte por
 * DENTRO de um item vai para `cortesInternos`, que a paginação só usa quando
 * o item sozinho é maior que a folha. Assim cada item cai inteiro numa folha
 * sem lista paralela de blocos: a unidade do papel é a mesma do catálogo.
 */

/** Âncora de item do catálogo da documentação (ver `catalogo.ts`). */
export const SELETOR_DE_ITEM = '[data-search-id]';

const PRAZO_DAS_IMAGENS_MS = 4000;

export interface Medida {
  altura: number;
  /** Cortes entre itens: os preferidos. */
  cortes: number[];
  /** Cortes por dentro de um item do catálogo: só se o item não couber numa folha. */
  cortesInternos: number[];
}

/** Altura útil de uma folha: a do corpo, entre o cabeçalho e o rodapé. */
export function alturaUtilDaFolha(folha: HTMLElement): number {
  const corpo = folha.querySelector<HTMLElement>('.ds-sheet-body');
  if (!corpo) throw new Error('Folha sem corpo.');
  return corpo.clientHeight;
}

/** Mede um bloco: altura total e cortes seguros, em px a partir do seu topo. */
export function medirBloco(raiz: HTMLElement): Medida {
  const caixa = raiz.getBoundingClientRect();
  const cortes = new Set<number>();
  const internos = new Set<number>();
  coletarCortes(raiz, caixa.top, { fora: cortes, dentro: internos }, false);
  const ordenar = (s: Set<number>) =>
    [...s].filter((c) => c > 0 && c < caixa.height).sort((a, b) => a - b);
  return { altura: caixa.height, cortes: ordenar(cortes), cortesInternos: ordenar(internos) };
}

interface Destino {
  fora: Set<number>;
  dentro: Set<number>;
}

function coletarCortes(
  elemento: Element,
  topo: number,
  destino: Destino,
  dentroDeItem: boolean,
): void {
  const noItem = dentroDeItem || elemento.matches(SELETOR_DE_ITEM);
  const cortes = noItem ? destino.dentro : destino.fora;
  // Texto pré-formatado (listagem de código) tem linhas de altura igual e
  // nada ao lado delas: corta-se entre uma linha e a seguinte.
  if (estiloDe(elemento).whiteSpace.startsWith('pre')) {
    coletarLinhas(elemento, topo, cortes);
    return;
  }
  if (temTextoSolto(elemento)) return;

  const filhos = filhosQueOcupamEspaco(elemento);
  if (filhos.length === 0) return;
  // Filho em linha de texto (selo, ligação, ícone) quebra junto com o texto: atômico.
  if (filhos.some((f) => estiloDe(f).display.startsWith('inline'))) return;

  const linhas: { topo: number; base: number; membros: Element[] }[] = [];
  const caixas = filhos
    .map((f) => ({ f, c: f.getBoundingClientRect() }))
    .sort((a, b) => a.c.top - b.c.top);
  for (const { f, c } of caixas) {
    const ultima = linhas[linhas.length - 1];
    if (ultima && c.top < ultima.base - 0.5) {
      ultima.base = Math.max(ultima.base, c.bottom);
      ultima.membros.push(f);
    } else {
      linhas.push({ topo: c.top, base: c.bottom, membros: [f] });
    }
  }

  linhas.forEach((linha, indice) => {
    if (indice > 0) cortes.add(linha.topo - topo);
    if (linha.membros.length === 1) coletarCortes(linha.membros[0]!, topo, destino, noItem);
  });
}

/** Topo de cada linha de texto do elemento, menos a primeira. */
function coletarLinhas(elemento: Element, topo: number, cortes: Set<number>): void {
  const faixa = elemento.ownerDocument.createRange();
  faixa.selectNodeContents(elemento);
  if (typeof faixa.getClientRects !== 'function') return;
  const topos = Array.from(faixa.getClientRects())
    .filter((r) => r.height > 0)
    .map((r) => r.top)
    .sort((a, b) => a - b);
  let anterior: number | undefined;
  for (const t of topos) {
    if (anterior === undefined || t - anterior > 1) {
      if (anterior !== undefined) cortes.add(t - topo);
      anterior = t;
    }
  }
}

function estiloDe(elemento: Element): CSSStyleDeclaration {
  return (elemento.ownerDocument.defaultView ?? window).getComputedStyle(elemento);
}

/** `instanceof HTMLElement` falha para elemento de outro documento (outro reino). */
function eHtml(elemento: Element): elemento is HTMLElement {
  return typeof (elemento as HTMLElement).offsetHeight === 'number';
}

function temTextoSolto(elemento: Element): boolean {
  return Array.from(elemento.childNodes).some(
    (no) => no.nodeType === Node.TEXT_NODE && (no.textContent ?? '').trim() !== '',
  );
}

/** Filhos HTML no fluxo e com altura; `display: contents` é atravessado. */
function filhosQueOcupamEspaco(elemento: Element): Element[] {
  const resultado: Element[] = [];
  for (const filho of Array.from(elemento.children)) {
    // Só HTML: SVG (gráfico) é desenho, não se corta por dentro.
    if (!eHtml(filho)) continue;
    const estilo = estiloDe(filho);
    if (estilo.display === 'none') continue;
    if (estilo.display === 'contents') {
      resultado.push(...filhosQueOcupamEspaco(filho));
      continue;
    }
    if (estilo.position === 'absolute' || estilo.position === 'fixed') continue;
    if (filho.getBoundingClientRect().height <= 0) continue;
    resultado.push(filho);
  }
  return resultado;
}

/**
 * Espera o que muda a altura depois da primeira pintura: fontes (a medição
 * com a fonte de reserva erra a quebra de linha), imagens e dois quadros de
 * animação para os gráficos responsivos tomarem a largura do contêiner.
 */
export async function aguardarRecursos(raiz: HTMLElement): Promise<void> {
  // A fonte só começa a carregar quando algum texto que a usa é diagramado, e
  // `fonts.ready` de um documento que ainda não diagramou nada já vem
  // resolvido: mede-se com a fonte de reserva e a quebra de linha sai outra.
  // Por isso força-se a diagramação antes e espera-se até não haver carga.
  void raiz.offsetHeight;
  const fontes = raiz.ownerDocument.fonts;
  if (fontes?.ready) {
    await fontes.ready;
    for (let i = 0; i < 20 && fontes.status === 'loading'; i++) await fontes.ready;
  }
  const imagens = Array.from(raiz.querySelectorAll('img'));
  // Imagem que nunca carrega (endereço quebrado) não pode prender o documento.
  const prazo = new Promise<void>((resolver) => setTimeout(resolver, PRAZO_DAS_IMAGENS_MS));
  await Promise.race([
    Promise.all(
      imagens.map((img) =>
        img.complete ? Promise.resolve() : img.decode().catch(() => undefined),
      ),
    ),
    prazo,
  ]);
  await proximoQuadro();
  await proximoQuadro();
}

function proximoQuadro(): Promise<void> {
  return new Promise((resolver) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolver());
    else setTimeout(resolver, 0);
  });
}
